if (!customElements.get("variant-options")) {
  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);
      constructor() {
        super();
        this.addEventListener("change", this.onVariantChange);
        this.addEventListener("keydown", this.onKeyDown);

        this.container = document.querySelector(
          `section[data-product-handle="${this.getAttribute("data-product-handle")}"]`,
        );
      }

      connectedCallback() {
        this.whenLoaded.then(() => {
          this.initialize();
        });
      }

      disconnectedCallback() {}

      initialize() {
        this.sortOptionsByPrice();
        this.autoSelectAvailable();
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updateVariantStatuses();
        this.updateDropdownButtons();
        this.applyCurrentVariantState(true);
      }

      sortOptionsByPrice() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        fieldsets.forEach((fieldset) => {
          const list = fieldset.querySelector("ul");
          if (!list) return;
          
          const items = Array.from(list.querySelectorAll("li"));
          const hasSortPrice = items.some(item => {
            const input = item.querySelector("input");
            return input && input.hasAttribute("data-sort-price");
          });
          
          if (hasSortPrice) {
            items.sort((a, b) => {
              const inputA = a.querySelector("input");
              const inputB = b.querySelector("input");
              const priceA = inputA ? parseInt(inputA.getAttribute("data-sort-price") || 0) : 0;
              const priceB = inputB ? parseInt(inputB.getAttribute("data-sort-price") || 0) : 0;
              return priceA - priceB;
            });
            items.forEach(item => list.appendChild(item));
          }
        });
      }

      autoSelectAvailable() {
        let changed = false;
        let lastChangedInput = null;
        const defaultVariantLabel = this.getAttribute('data-default-variant')?.trim().toLowerCase();

        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        fieldsets.forEach((fieldset) => {
          let hasHandledDefault = false;
          if (defaultVariantLabel) {
            const allInputs = Array.from(fieldset.querySelectorAll("input[type='radio']"));
            const defaultInput = allInputs.find(input => 
              input.value.trim().toLowerCase() === defaultVariantLabel &&
              input.dataset.forceUnavailable !== "true"
            );
            if (defaultInput) {
              if (!defaultInput.checked) {
                defaultInput.checked = true;
                changed = true;
                lastChangedInput = defaultInput;
              }
              hasHandledDefault = true;
            }
          }
          
          if (!hasHandledDefault) {
            const checkedInput = fieldset.querySelector("input:checked");
            if (!checkedInput || (checkedInput && checkedInput.dataset.forceUnavailable === "true")) {
              const inputs = Array.from(fieldset.querySelectorAll("input[type='radio']:not([data-force-unavailable='true'])"));
              const firstAvailable = inputs.find(input => !input.classList.contains("disabled")) || inputs[0];
              if (firstAvailable && !firstAvailable.checked) {
                firstAvailable.checked = true;
                changed = true;
                lastChangedInput = firstAvailable;
              }
            }
          }
        });
        if (changed && lastChangedInput) {
          setTimeout(() => {
            lastChangedInput.dispatchEvent(new Event("change", { bubbles: true }));
          }, 10);
        }
      }

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          input?.click();
        }
      }

      onVariantChange() {
        const variantChangeStartEvent = new CustomEvent("variantChangeStart", {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(variantChangeStartEvent);
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.toggleAddButton(true, "", false);
        this.updatePickupAvailability();
        this.removeErrorMessage();
        this.updateVariantStatuses();
        this.updateDropdownButtons();

        if (!this.currentVariant) {
          this.toggleAddButton(true, "", true);
          this.setUnavailable();
        } else {
          this.updateMedia();
          this.lenOfVariantOptions =
            document.querySelectorAll("variant-options").length;
          if (this.lenOfVariantOptions === 1) {
            this.updateURL();
          }
          this.updateVariantInput();
          this.applyCurrentVariantState(false);
          this.updateShareUrl();
        }
        const variantChangeEndEvent = new CustomEvent("variantChangeEnd", {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(variantChangeEndEvent);
      }

      applyCurrentVariantState(isInitial = false) {
        if (!this.currentVariant) {
          this.toggleAddButton(true, "", true);
          this.setUnavailable();
          return;
        }

        const section = this.closest("section") || document;
        const priceContainer = document.getElementById(
          `price-${this.dataset.section}`,
        ) || section.querySelector('[id^="price-"]')
          || section.querySelector('.wt-product__price')
          || section.querySelector('.price');

        if (this.currentVariant.isVirtual) {
          if (!this.currentVariant.available) {
            this.toggleAddButton(true, window.variantStrings?.soldOut || 'Sold out');
            if (priceContainer) {
              priceContainer.classList.add('price--sold-out');
              const innerPrice = priceContainer.querySelector('.price') || priceContainer;
              innerPrice.classList.add('price--sold-out');
              const soldBadge = priceContainer.querySelector('.price__badge-sold-out');
              if (soldBadge) soldBadge.classList.remove('hidden');
            }
          } else {
            this.toggleAddButton(false);
            if (priceContainer) {
              priceContainer.classList.remove('price--sold-out');
              const innerPrice = priceContainer.querySelector('.price') || priceContainer;
              innerPrice.classList.remove('price--sold-out');
              const soldBadge = priceContainer.querySelector('.price__badge-sold-out');
              if (soldBadge) soldBadge.classList.add('hidden');
            }
          }

          if (priceContainer && this.currentVariant.formattedPrice) {
            const priceItems = priceContainer.querySelectorAll('.wt-product__price__final');
            if (priceItems.length > 0) {
              priceItems.forEach(item => item.innerHTML = this.currentVariant.formattedPrice);
            } else {
              const priceItem = priceContainer.querySelector('.price-item--regular, .price-item') || priceContainer;
              priceItem.innerHTML = this.currentVariant.formattedPrice;
            }

            const comparePriceItems = priceContainer.querySelectorAll('.wt-product__price__compare');
            const discountBadges = priceContainer.querySelectorAll('.price-item--percent');
            const saleContainer = priceContainer.querySelector('.price__sale');
            const innerPriceElement = priceContainer.querySelector('.price') || priceContainer;

            if (this.currentVariant.formattedComparePrice) {
              comparePriceItems.forEach(item => {
                item.innerHTML = this.currentVariant.formattedComparePrice;
                item.classList.remove('hidden');
              });
              discountBadges.forEach(badge => {
                badge.innerHTML = this.currentVariant.discountPerc;
                badge.classList.remove('hidden');
              });
              if (saleContainer) {
                saleContainer.classList.add('visible');
                if (this.dataset.section && this.dataset.section.includes('main')) {
                  saleContainer.classList.add('visible-main-product');
                }
              }
              innerPriceElement.classList.add('price--on-sale');
              priceContainer.classList.add('price--on-sale');
            } else {
              comparePriceItems.forEach(item => item.classList.add('hidden'));
              discountBadges.forEach(badge => badge.classList.add('hidden'));
              if (saleContainer) {
                saleContainer.classList.remove('visible', 'visible-main-product');
              }
              innerPriceElement.classList.remove('price--on-sale');
              priceContainer.classList.remove('price--on-sale');
            }

            priceContainer.classList.remove("visibility-hidden");
          }
          this.updateVariantInput();
        } else {
          if (isInitial) {
            if (this.currentVariant.available) {
              this.toggleAddButton(false);
              if (priceContainer) {
                priceContainer.classList.remove('price--sold-out');
                const innerPrice = priceContainer.querySelector('.price') || priceContainer;
                innerPrice.classList.remove('price--sold-out');
                const soldBadge = priceContainer.querySelector('.price__badge-sold-out');
                if (soldBadge) soldBadge.classList.add('hidden');
              }
            } else {
              this.toggleAddButton(true, window.variantStrings?.soldOut || 'Sold out');
            }
          } else {
            this.renderProductInfo();
          }
        }
      }

      updateGallery() {
        const mediaGallery = document.getElementById(
          `MediaGallery-${this.dataset.section}`,
        );

        let media_id = false;
        if (this.currentVariant && this.currentVariant.featured_media) {
          media_id = this.currentVariant.featured_media.id;
        }

        try{
          mediaGallery?.filterSlides(this.options, media_id, true);
        } catch (error) {}
      }

      updateDropdownButtons(){
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option"),
        );
        this.options = fieldsets.map((fieldset) => {
          return Array.from(fieldset.querySelectorAll("input")).find(
            (radio) => radio.checked,
          )?.value || '';
        });

        fieldsets.forEach((fieldset) => {
          const dropdown = fieldset.querySelector(
            ".wt-product__option__dropdown",
          );
          if (dropdown) {
            const checkedInput = fieldset.querySelector('fieldset input:checked')
            if(!checkedInput) return
            const isInputDisabled = checkedInput?.classList.contains('disabled')
            dropdown.classList.toggle('wt-product__option__dropdown--unavailable', isInputDisabled)
          }
        });
      }

      updateOptions() {
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option"),
        );
        this.options = fieldsets.map((fieldset) => {
          return (
            Array.from(fieldset.querySelectorAll("input")).find(
              (radio) => radio.checked,
            )?.value || ""
          );
        });

        this.checkedOptions = fieldsets.map((fieldset) => {
          return Array.from(fieldset.querySelectorAll("input")).find(
            (radio) => radio.checked,
          ) || '';
        });

        fieldsets.forEach((fieldset, index) => {
          const selectedOption = this.options[index];

          if (!selectedOption) return;

          fieldset.querySelector(
            ".wt-product__option__title .value",
          ).innerHTML = selectedOption;
          const dropdownSpan = fieldset.querySelector(
            ".wt-product__option__dropdown span",
          );
          if (dropdownSpan) dropdownSpan.innerHTML = selectedOption;
        });
      }

      updateMasterId() {
        const checkedVirtual = this.querySelector('input[data-is-virtual="true"]:checked')
          || this.querySelector('.wt-product__option input[data-is-virtual="true"]:checked');

        if (checkedVirtual) {
          const vCompId = parseInt(checkedVirtual.getAttribute("data-virtual-component-id"));
          const vCompVariant = this.getVariantData()?.find(v => v.id === vCompId);
          const isAvailable = vCompVariant ? Boolean(vCompVariant._real_available !== undefined ? vCompVariant._real_available : vCompVariant.available) : !checkedVirtual.classList.contains("disabled");
          this.currentVariant = {
            id: vCompId,
            price: parseInt(checkedVirtual.getAttribute("data-virtual-price-cents")),
            available: isAvailable,
            isVirtual: true,
            options: [checkedVirtual.value],
            formattedPrice: checkedVirtual.getAttribute("data-virtual-price-formatted"),
            formattedComparePrice: checkedVirtual.getAttribute("data-virtual-compare-price-formatted") || null,
            discountPerc: checkedVirtual.getAttribute("data-virtual-discount-perc") || null,
            componentQty: parseInt(checkedVirtual.getAttribute("data-virtual-component-qty")),
            title: checkedVirtual.value
          };
          return;
        }
        this.currentVariant = this.getVariantData()?.find((variant) => {
          return !variant.options
            .map((option, index) => {
              return this.options[index] === option;
            })
            .includes(false);
        });
      }

      updateMedia() {
        if (!this.currentVariant) return;
        this.setAttribute("data-variant-id", this.currentVariant?.id);
        if (!this.currentVariant.featured_media) return;
        this.setAttribute(
          "data-featured-image",
          this.currentVariant?.featured_media?.preview_image?.src,
        );
        this.setAttribute(
          "data-featured-image-id",
          this.currentVariant?.featured_media?.id,
        );

        const modalContent = document.querySelector(
          `#ProductModal-${this.dataset.section} .product-media-modal__content`,
        );
        if (!modalContent) return;
        const newMediaModal = modalContent.querySelector(
          `[data-media-id="${this.currentVariant.featured_media.id}"]`,
        );
        modalContent.prepend(newMediaModal);
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateShareUrl() {
        const shareButton = document.getElementById(
          `Share-${this.dataset.section}`,
        );
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateVariantInput() {
        const section = this.closest("section") || document;
        const productForms = section.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, form[id^="product-form-"], form[data-type="add-to-cart-form"], .wt-product__add-to-cart_form`
        );
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input && this.currentVariant) {
            input.value = this.currentVariant.id;
            input.removeAttribute("disabled");
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }

      updateVariantStatuses() {
        // const selectedOptionOneVariants = this.variantData.filter(
        const selectedOptionOneVariants = this.getVariantData()?.filter(
          (variant) => variant.available === true,
        );
        const inputWrappers = [
          ...this.querySelectorAll(".product-form__input"),
        ];

        const checkedInputs = [...this.querySelectorAll('.product-form__input :checked')]
        const checkedInputsValues = [...this.querySelectorAll('.product-form__input :checked')].map(input => input.getAttribute('value'))

        const previousSelectedOptions = []
        inputWrappers.forEach((option, index) => {
          if (index === 0 && this.currentVariant?.options.length > 1) return;

          const optionInputs = [
            ...option.querySelectorAll('input[type="radio"], option'),
          ];
          const previousOptionSelected =
            inputWrappers[index - 1]?.querySelector(":checked")?.value;
          
            previousSelectedOptions.push(previousOptionSelected)

              const availableOptionInputsValue = selectedOptionOneVariants
              .filter(
                (variant) => {
                  if (index === 2){
                    return variant.available &&
                    variant[`option1`] === previousSelectedOptions[0] &&
                    variant[`option2`] === previousSelectedOptions[1]
                  } else {
                    return variant.available &&
                    variant[`option${index}`] === previousOptionSelected
                  }
                }
              )
              .map((variantOption) => variantOption[`option${index + 1}`]);
              this.setInputAvailability(optionInputs, availableOptionInputsValue, checkedInputsValues, index, checkedInputs);
        });
      }

      setInputAvailability(listOfOptions, listOfAvailableOptions, checkedInputsValues, index, checkedInputs) {
        // helper function to check if array contains another array
        function containsSubarray(arr, subarr) {
          return arr.join(',').includes(subarr.join(','));
        }
        
        const checkedValues = checkedInputsValues.slice(0, index)

        const listOfAvailableVariants= this.getVariantData().filter(variant => {
            if(containsSubarray(variant.options, checkedValues)) return true
        })

        const isPreviousOptionChecked = checkedInputs.find(input => input.dataset.position === `${index}`) || index === 0
        

        listOfOptions.forEach((input) => {          
          if(!isPreviousOptionChecked) return;
          if (input.getAttribute("data-is-virtual") === "true") {
            const vCompId = input.getAttribute("data-virtual-component-id");
            if (vCompId) {
              const vCompVariant = this.getVariantData().find(v => v.id == vCompId);
              const isAvailable = vCompVariant ? Boolean(vCompVariant._real_available !== undefined ? vCompVariant._real_available : vCompVariant.available) : true;
              if (vCompVariant && !isAvailable) {
                input.classList.add("disabled");
                return;
              }
            }
            input.classList.remove("disabled");
            return;
          }
          if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
            input.classList.remove("disabled");
          } else {
            input.classList.add("disabled");
          }

          // check if option exist. If not, it should be disabled
          let inputOccurs = false;
          listOfAvailableVariants.forEach(variant => {
            if(variant.options.includes(input.getAttribute("value"))) {
              inputOccurs = true
            }
          })

          if(!inputOccurs) input.classList.add("disabled");

        });
      }

      updatePickupAvailability() {
        const pickUpAvailability = document.querySelector(
          "pickup-availability",
        );
        if (!pickUpAvailability) return;

        pickUpAvailability.dataset.variantId = this.currentVariant?.id;
        if (this.currentVariant && this.currentVariant.available) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
          pickUpAvailability.removeAttribute("available");
          pickUpAvailability.innerHTML = "";
        }
      }

      removeErrorMessage() {
        const section = this.closest("section");
        if (!section) return;

        const productForm = section.querySelector("product-form");
        try {
          productForm?.handleErrorMessage();
        } catch (err) {
          console.log(err);
        }
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant?.id;
        const sectionId = this.dataset.originalSection
          ? this.dataset.originalSection
          : this.dataset.section;

        fetch(
          `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
            this.dataset.originalSection
              ? this.dataset.originalSection
              : this.dataset.section
          }`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            // prevent unnecessary ui changes from abandoned selections
            if (this.currentVariant?.id !== requestedVariantId) return;

            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const destination = document.getElementById(
              `price-${this.dataset.section}`,
            );
            const source = html.getElementById(
              `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`,
            );
            const skuSource = html.getElementById(
              `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`,
            );
            const skuDestination = document.getElementById(
              `Sku-${this.dataset.section}`,
            );
            const inventorySource = html.getElementById(
              `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`,
            );
            const inventoryDestination = document.getElementById(
              `Inventory-${this.dataset.section}`,
            );

            if (source && destination) destination.innerHTML = source.innerHTML;
            if (inventorySource && inventoryDestination)
              inventoryDestination.innerHTML = inventorySource.innerHTML;
            if (skuSource && skuDestination) {
              skuDestination.innerHTML = skuSource.innerHTML;
              skuDestination.classList.toggle(
                "visibility-hidden",
                skuSource.classList.contains("visibility-hidden"),
              );
            }

            const price = document.getElementById(
              `price-${this.dataset.section}`,
            );

            if (price) price.classList.remove("visibility-hidden");

            if (inventoryDestination)
              inventoryDestination.classList.toggle(
                "visibility-hidden",
                inventorySource.innerText === "",
              );

            const addButtonUpdated = html.getElementById(
              `ProductSubmitButton-${sectionId}`,
            );
            const isForcedDisabled = this.currentVariant && this.currentVariant.available === false;
            this.toggleAddButton(
              isForcedDisabled || (addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true),
              window.variantStrings.soldOut,
            );

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: sectionId,
                html,
                variant:this.currentVariant
              },
            });
          });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const section = this.closest("section") || document;
        const productForms = section.querySelectorAll(
          `#product-form-${this.dataset.section}, product-form, form[id^="product-form-"], form[data-type="add-to-cart-form"], .wt-product__add-to-cart_form`
        );
        productForms.forEach((productForm) => {
          const addButtons = productForm.querySelectorAll(
            '[name="add"], .js-add-to-cart, .product-form__submit',
          );
          addButtons.forEach((addButton) => {
            const addButtonText = addButton.querySelector('span') || addButton;
            if (disable) {
              addButton.setAttribute("disabled", "disabled");
              addButton.setAttribute("aria-disabled", "true");
              if (text) addButtonText.textContent = text;
            } else {
              addButton.removeAttribute("disabled");
              addButton.removeAttribute("aria-disabled");
              addButton.setAttribute("aria-disabled", "false");
              if (window.variantStrings?.addToCart) {
                addButtonText.textContent = window.variantStrings.addToCart;
              }
            }
          });
        });

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(
          `product-form-${this.dataset.section}`,
        );
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(
          `Inventory-${this.dataset.section}`,
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = window.variantStrings.unavailable;
        if (price) price.classList.add("visibility-hidden");
        if (inventory) inventory.classList.add("visibility-hidden");
        if (sku) sku.classList.add("visibility-hidden");
      }

      getVariantData() {
        if (!this.variantData) {
          this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
          
          // Store real availability before any UI-forced overrides
          this.variantData.forEach(v => v._real_available = v.available);
          
          const forcedUnavailableInputs = Array.from(this.querySelectorAll('input[data-force-unavailable="true"]'));
          const forcedValues = forcedUnavailableInputs.map(input => input.value);
          if (forcedValues.length > 0) {
            this.variantData.forEach(variant => {
              const matchesForced = variant.options.some(opt => forcedValues.includes(opt));
              if (matchesForced) {
                variant.available = false;
              }
            });
          }
        }
        return this.variantData;
      }
    },
  );
}
// customElements.define('variant-options', VariantOptions);
